import {
  readBreadcrumbSessionRecord,
  writeBreadcrumbSessionRecord,
} from './breadcrumbSessionStorage'
import { APPConfig } from 'config/appConfig'
import { getLocaleCodeFromUrl } from './localeUtils'

export const getBreadcrumbSessionRegion = () => {
  const defaultLocale =
    APPConfig?.getActiveAppConfig?.()?.defaultLocale ||
    APPConfig?.getAppConfig?.()?.defaultLocale ||
    'en_US'

  if (typeof window === 'undefined') return defaultLocale

  try {
    return getLocaleCodeFromUrl({
      isReverseType: true,
      defaultLocale,
    })
  } catch (e) {
    console.warn(
      '[breadcrumb] getLocaleCodeFromUrl failed, using default locale:',
      e
    )
    return defaultLocale
  }
}

const hasPresentValue = value =>
  value != null && (typeof value !== 'string' || value.trim().length > 0)

const isNonArrayObject = value =>
  value && typeof value === 'object' && !Array.isArray(value)

const getBreadcrumbLabel = node => {
  if (hasPresentValue(node?.label)) return node.label.toString()
  if (hasPresentValue(node?.title)) return node.title.toString()
  return ''
}

const getBreadcrumbHref = node => {
  if (hasPresentValue(node?.link)) return node.link.toString()
  if (hasPresentValue(node?.href)) return node.href.toString()
  return '/'
}

const hasBreadcrumbContent = node =>
  hasPresentValue(node?.label) ||
  hasPresentValue(node?.title) ||
  hasPresentValue(node?.href) ||
  hasPresentValue(node?.link)

const normalizeBreadcrumbNode = node => ({
  label: getBreadcrumbLabel(node),
  href: getBreadcrumbHref(node),
})

const normalizeBreadcrumbNodes = nodes =>
  (nodes || []).filter(hasBreadcrumbContent).map(normalizeBreadcrumbNode)

const collectSubCategoryRecords = (addRecord, top, sub) => {
  const subPath = [top, sub]
  addRecord(sub?.identifier, subPath)
  addRecord(extractCatalogTokenFromHref(sub?.href), subPath)
  ;(sub?.items || []).forEach(item => {
    const itemPath = [top, sub, item]
    addRecord(extractCatalogTokenFromHref(item?.href), itemPath)
  })
}

const collectTopCategoryRecords = (addRecord, top) => {
  const topPath = [top]
  addRecord(top?.identifier, topPath)
  addRecord(extractCatalogTokenFromHref(top?.href), topPath)
  ;(top?.subCategories || []).forEach(sub => {
    collectSubCategoryRecords(addRecord, top, sub)
  })
}

const normalizePathMapEntries = parsed => {
  const normalized = {}

  Object.entries(parsed).forEach(([token, serializedPath]) => {
    const normalizedToken = normalizeBreadcrumbToken(token)
    if (!normalizedToken || normalized[normalizedToken]) return

    const pathNodes = parseBreadcrumbPath(serializedPath)
    if (pathNodes.length === 0) return

    normalized[normalizedToken] = pathNodes
  })

  return Object.keys(normalized).length > 0 ? normalized : null
}

export const serializeBreadcrumbPath = pathNodes =>
  JSON.stringify(normalizeBreadcrumbNodes(pathNodes))

export const parseBreadcrumbPath = serializedPath => {
  if (!serializedPath) return []

  try {
    const parsed = JSON.parse(serializedPath)
    if (Array.isArray(parsed)) {
      return normalizeBreadcrumbNodes(parsed)
    }
  } catch {
    return []
  }

  return []
}

export const normalizeBreadcrumbToken = value =>
  (value || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '')

export const extractCatalogTokenFromHref = href => {
  const match = (href || '').match(/\/(catalog|categoryLanding)\/([^/?#]+)/i)
  return match?.[2] || ''
}

export const serializeNavigationPathRecords = categories => {
  const records = {}
  const seen = new Set()

  const addRecord = (token, pathNodes) => {
    const normalizedToken = normalizeBreadcrumbToken(token)
    if (!normalizedToken || seen.has(normalizedToken)) return

    const serializedPath = serializeBreadcrumbPath(pathNodes)
    if (!serializedPath) return

    seen.add(normalizedToken)
    records[normalizedToken] = serializedPath
  }

  ;(categories || []).forEach(top => {
    collectTopCategoryRecords(addRecord, top)
  })

  return JSON.stringify(records)
}

export const parseNavigationPathRecords = records => {
  if (!records || typeof records !== 'string') return null

  try {
    const parsed = JSON.parse(records)
    if (!isNonArrayObject(parsed)) return null

    return normalizePathMapEntries(parsed)
  } catch {
    return null
  }
}

export { readBreadcrumbSessionRecord, writeBreadcrumbSessionRecord }
